import DeleteIcon from "@shared/svg/delete-icon.svg?react";
import InfoIcon from "@shared/svg/info-icon.svg?react";
import {
  EXTERNAL_ICON_SIZES,
  ExternalIcon,
  EXTERNAL_ICON_THUMB_BADGE_CLASS,
} from "@shared/ui/Indicators";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@shared/ui/Button";
import { Counter, CounterPropsType } from "@shared/ui/Inputs";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import { useIntl } from "react-intl";

type PartListItemPropsType = {
  articleNumber: string;
  name: string;
  thumbnail: string;
  visibilityScope?: string;
  hideDetailsButton?: boolean;
  onButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  showDeleteButton: boolean;
  onDeleteButtonClick: React.MouseEventHandler<HTMLButtonElement>;
  counterProps: CounterPropsType;
  customLabelStyles?: string;
  errorMessage?: string;
  errorVariant?: "danger" | "warning";
  parentAsset?: string;
};

const PartListItem = ({
  articleNumber,
  name,
  thumbnail,
  visibilityScope,
  parentAsset,
  hideDetailsButton = false,
  onButtonClick = () => {},
  showDeleteButton,
  onDeleteButtonClick,
  counterProps,
  customLabelStyles = "",
  errorMessage = "",
  errorVariant = "warning",
}: PartListItemPropsType) => {
  const { messages } = useIntl();
  const hasError = Boolean(errorMessage);
  const containerClasses = `flex flex-col cursor-pointer mb-lg border border-solid border-primary bg-primary hover:bg-accent rounded-lg overflow-hidden`;
  const contentClasses =
    "flex flex-col sm:flex-row items-start sm:items-center p-md max-sm:gap-md justify-between";
  const errorBackgroundClass =
    errorVariant === "danger" ? "bg-danger-lightest" : "bg-warning-lightest";
  const errorTextClass =
    errorVariant === "danger" ? "text-danger" : "text-warning";
  const infoIconStrokeColor =
    errorVariant === "danger" ? "stroke-danger" : "stroke-warning";
  return (
    <div className={containerClasses}>
      <div className={contentClasses}>
        <div className="flex items-center gap-lg w-1/2">
          <div className="relative h-4xl w-5xl shrink-0 overflow-visible mr-lg">
            <div className="h-full w-full overflow-hidden rounded-lg">
              <img
                src={thumbnail}
                alt={name}
                className="h-full w-full object-cover object-center"
              />
            </div>
            {String(visibilityScope || "").toLowerCase() === "external" ? (
              <ExternalIcon
                size={EXTERNAL_ICON_SIZES.ACCORDION}
                className={EXTERNAL_ICON_THUMB_BADGE_CLASS}
              />
            ) : null}
          </div>
          {parentAsset ? (
            <div className="flex flex-col overflow-hidden">
              <Label size={LABEL_SIZES.MEDIUM} className="truncate max-w-48">
                {name}
                <span className="text-secondary"> • {articleNumber} </span>
              </Label>
              <BodyText
                size={BODY_TEXT_SIZES.X_SMALL}
                color="text-secondary"
                className="truncate max-w-48"
              >
                {parentAsset}
              </BodyText>
            </div>
          ) : (
            <div
              className={`flex flex-col overflow-hidden ${customLabelStyles}`}
            >
              <Label
                size={LABEL_SIZES.SMALL}
                color="text-secondary"
                className="truncate max-w-48"
              >
                {articleNumber}
              </Label>
              <Label size={LABEL_SIZES.SMALL} className="truncate max-w-48">
                {name}
              </Label>
              {!hideDetailsButton && (
                <div>
                  <Button
                    variant={BUTTON_VARIANTS.LINK}
                    text={messages.common.details}
                    onClick={onButtonClick}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-lg max-sm:w-full max-sm:justify-between max-sm:flex-row-reverse">
          {showDeleteButton && (
            <Button
              variant={BUTTON_VARIANTS.OUTLINE}
              className="!bg-primary"
              size={BUTTON_SIZES.SMALL}
              leadingIcon={<DeleteIcon width={16} height={16} />}
              onClick={onDeleteButtonClick}
            />
          )}
          <Counter
            count={counterProps.count}
            setCount={counterProps.setCount}
            incrementCount={counterProps.incrementCount}
            decrementCount={counterProps.decrementCount}
            isReadOnlyInput={counterProps.isReadOnlyInput}
          />
        </div>
      </div>
      {hasError ? (
        <div
          className={`w-full flex items-center px-md py-sm ${errorBackgroundClass}`}
        >
          <InfoIcon className={infoIconStrokeColor} />

          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color={errorTextClass}
            className="ml-sm"
          >
            {errorMessage}
          </BodyText>
        </div>
      ) : null}
    </div>
  );
};

export default PartListItem;
